import { savedPlayerName } from "../identity/playerIdentityGate";
import { PROFILE_GENDERS } from "../identity/playerProfileConstants";
import { resolveEventCompetition, PARTICIPATION_GENDER } from "../event/eventCompetition";
import {
  ageInWholeYears,
  formatAgeBandLabel,
  playerAgeMeetsBand,
  resolveParticipationAgeBand,
} from "../event/participationAge";

const EMPTY_ASK = Object.freeze({
  selfName: false,
  selfSex: false,
  partnerSex: false,
  selfDob: false,
  partnerDob: false,
});

/**
 * @param {string | null | undefined} gender
 * @returns {"male" | "female" | ""}
 */
export function sexBucket(gender) {
  const value = String(gender || "").trim().toLowerCase();
  if (value === "male" || value === "m") return "male";
  if (value === "female" || value === "f") return "female";
  return "";
}

/** Canonical profile gender, including Non-binary and Prefer not to say. */
export function profileSexLabel(gender) {
  const value = String(gender || "").trim();
  if (!value) return "";
  const match = PROFILE_GENDERS.find(
    (option) => option && option.toLowerCase() === value.toLowerCase()
  );
  if (match) return match;
  const sex = sexBucket(value);
  if (sex === "male") return "Male";
  if (sex === "female") return "Female";
  return "";
}

/**
 * Whether a found player can be chosen as partner for this event.
 * Men's and women's doubles stay same-sex. Mixed doubles stays one of each.
 * A profile without Male or Female cannot be chosen for those events.
 * @param {object | null | undefined} player
 * @param {{ selfGender?: string | null, genderRule?: string | null }} [opts]
 * @returns {{ ok: boolean, reason: string }}
 */
export function partnerMayJoin(player, opts = {}) {
  const sex = sexBucket(player?.gender);
  const self = sexBucket(opts.selfGender);
  const rule = String(opts.genderRule || "");
  if (!rule || rule === PARTICIPATION_GENDER.OPEN) return { ok: true, reason: "" };
  if (rule === PARTICIPATION_GENDER.MEN) {
    if (sex === "male") return { ok: true, reason: "" };
    if (sex === "female") return { ok: false, reason: "Men's events need a male partner." };
    return { ok: false, reason: "Gender on this profile is not Male." };
  }
  if (rule === PARTICIPATION_GENDER.WOMEN) {
    if (sex === "female") return { ok: true, reason: "" };
    if (sex === "male") return { ok: false, reason: "Women's events need a female partner." };
    return { ok: false, reason: "Gender on this profile is not Female." };
  }
  if (rule === PARTICIPATION_GENDER.MIXED) {
    if (!self) return { ok: false, reason: "Add your gender before choosing a partner." };
    if (!sex) return { ok: false, reason: "Gender on this profile is not Male or Female." };
    if (sex === self) return { ok: false, reason: "Mixed doubles needs a partner of the other gender." };
    return { ok: true, reason: "" };
  }
  return { ok: true, reason: "" };
}

/**
 * @param {string | null | undefined} gender
 * @param {string | null | undefined} rule
 * @param {string} eventName
 * @param {"self" | "partner"} who
 * @returns {{ ask?: "selfSex" | "partnerSex", blocked?: string } | null}
 */
function genderIssue(gender, rule, eventName, who) {
  const sex = sexBucket(gender);
  const ask = who === "partner" ? "partnerSex" : "selfSex";
  if (!rule || rule === PARTICIPATION_GENDER.OPEN) return null;
  if (rule === PARTICIPATION_GENDER.MEN) {
    if (sex === "male") return null;
    if (!sex) return { ask };
    return { blocked: `${eventName} is for men.` };
  }
  if (rule === PARTICIPATION_GENDER.WOMEN) {
    if (sex === "female") return null;
    if (!sex) return { ask };
    return { blocked: `${eventName} is for women.` };
  }
  if (rule === PARTICIPATION_GENDER.MIXED) {
    if (sex === "male" || sex === "female") return null;
    return { ask };
  }
  return null;
}

/**
 * @param {string | null | undefined} dob
 * @param {import("../event/participationAge").AgeBandRule | null} band
 * @param {number} asOfMs
 * @param {string} eventName
 * @param {"self" | "partner"} who
 */
function ageIssue(dob, band, asOfMs, eventName, who) {
  if (!band) return null;
  const ms = dob ? Date.parse(String(dob)) : NaN;
  const age = Number.isFinite(ms) ? ageInWholeYears(ms, asOfMs) : null;
  const check = playerAgeMeetsBand(age, band);
  if (check.ok) return null;
  if (age == null) return { ask: who === "partner" ? "partnerDob" : "selfDob" };
  const label = formatAgeBandLabel(band);
  const subject = who === "partner" ? "Your partner" : "You";
  if (band.minAge != null && age < band.minAge) {
    return { blocked: `${eventName} is ${label}. ${subject} must be at least ${band.minAge}.` };
  }
  if (band.maxAge != null && age > band.maxAge) {
    return { blocked: `${eventName} is ${label}. ${subject} must be ${band.maxAge} or under.` };
  }
  return { blocked: check.reason || `${eventName} does not match this age category.` };
}

/**
 * Backstop for men's and women's events. Mixed pairs stay in entry validation.
 * @param {Array<{ gender?: string | null }> | null | undefined} members
 * @param {string | null | undefined} genderRule
 * @param {string} [eventName]
 * @returns {string | null}
 */
export function participationGenderError(members, genderRule, eventName = "This event") {
  const rule = String(genderRule || "");
  if (rule !== PARTICIPATION_GENDER.MEN && rule !== PARTICIPATION_GENDER.WOMEN) return null;
  const need = rule === PARTICIPATION_GENDER.MEN ? "male" : "female";
  const label = rule === PARTICIPATION_GENDER.MEN ? "men" : "women";
  const people = Array.isArray(members) ? members : [];
  if (people.some((member) => sexBucket(member?.gender) !== need)) {
    return `${eventName} is for ${label}.`;
  }
  return null;
}

/**
 * What still blocks registration, or which fields the popup should ask for.
 * A known mismatch (a man in a women's event) is a message.
 * An empty sex or date of birth is asked after the player has filled the form.
 *
 * @param {{
 *   event: object,
 *   self?: object | null,
 *   partner?: object | null,
 *   needsPartner?: boolean,
 *   asOfMs?: number,
 * }} input
 */
export function assessEventRegistration(input) {
  const event = input?.event;
  const eventName = String(event?.name || event?.display?.name || "This event");
  const participation = resolveEventCompetition(event)?.participation;
  const rule = participation?.gender || PARTICIPATION_GENDER.OPEN;
  const band = resolveParticipationAgeBand(participation);
  const asOfMs = input.asOfMs || Date.now();
  const self = input.self || {};
  const partner = input.partner || {};
  const needsPartner = Boolean(input.needsPartner);

  /** @type {string | null} */
  let blocked = null;
  const ask = { ...EMPTY_ASK };

  const selfGender = genderIssue(self.gender, rule, eventName, "self");
  if (selfGender?.blocked) blocked = selfGender.blocked;
  const selfAge = ageIssue(self.dateOfBirth, band, asOfMs, eventName, "self");
  if (!blocked && selfAge?.blocked) blocked = selfAge.blocked;

  if (needsPartner) {
    const partnerGender = genderIssue(partner.gender, rule, eventName, "partner");
    if (!blocked && partnerGender?.blocked) blocked = partnerGender.blocked;
    const partnerAge = ageIssue(partner.dateOfBirth, band, asOfMs, eventName, "partner");
    if (!blocked && partnerAge?.blocked) blocked = partnerAge.blocked;
    if (
      !blocked &&
      rule === PARTICIPATION_GENDER.MIXED &&
      sexBucket(self.gender) &&
      sexBucket(partner.gender) &&
      sexBucket(self.gender) === sexBucket(partner.gender)
    ) {
      blocked = `${eventName} needs one man and one woman.`;
    }
  }

  if (blocked) {
    return { ok: false, blocked, ask: { ...EMPTY_ASK } };
  }

  if (!savedPlayerName(self)) ask.selfName = true;
  if (selfGender?.ask) ask[selfGender.ask] = true;
  if (selfAge?.ask) ask[selfAge.ask] = true;
  if (needsPartner) {
    const partnerGender = genderIssue(partner.gender, rule, eventName, "partner");
    const partnerAge = ageIssue(partner.dateOfBirth, band, asOfMs, eventName, "partner");
    if (partnerGender?.ask) ask[partnerGender.ask] = true;
    if (partnerAge?.ask) ask[partnerAge.ask] = true;
  }

  const needsAnswer = Object.values(ask).some(Boolean);
  return { ok: !needsAnswer, blocked: null, ask };
}
